import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Dropdown,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Spin,
  Statistic,
  Table,
  Tag,
  TimePicker,
  Timeline,
  Typography,
  message,
} from "antd";
import {
  AlertOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  EyeOutlined,
  FileTextOutlined,
  LogoutOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  ShareAltOutlined,
  ShopOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import ChangePasswordForm from "../../components/ChangePasswordForm";
import PatientNotificationBell from "../../components/PatientNotificationBell";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useModules } from "../../context/ModuleContext";
import {
  addProxyUser,
  cancelAppointment,
  getAvailableDoctors,
  getAllPharmacies,
  getAvailablePharmacies,
  getBillingRecords,
  getEMSRequests,
  getMyDiagnoses,
  getOpticalServices,
  getPatientAppointments,
  getPatientProfile,
  getPrescriptions,
  getProxyUsers,
  getShareConsents,
  requestAppointment,
  requestEMS,
  revokeShareConsent,
  shareRecords,
  updatePatientProfile,
} from "../../services/patientService";
import {
  getPublicPartnerOrgs,
  updatePrescriptionPickup,
} from "../../services/partnerOrgService";
import { getNetworkHospitals, getNetworkDoctors } from "../../services/operationsService";
import { createTicket, getMyTickets } from "../../services/supportService";

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

const toArray = (d) => Array.isArray(d) ? d : (d?.results ?? []);
const toCount = (d) => Array.isArray(d) ? d.length : (d?.count ?? d?.results?.length ?? 0); // eslint-disable-line no-unused-vars
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";

const EMS_COLOR = { PENDING:"orange", DISPATCHED:"blue", IN_TRANSIT:"purple", COMPLETED:"green", CANCELLED:"default" };
const BILLING_COLOR = { DRAFT:"default", SUBMITTED:"blue", APPROVED:"green", REJECTED:"red", PAID:"cyan" };


// RX_COLS is built as a function so it can reference openPickupModal from component scope.
// The function is called once inside the component where openPickupModal is in scope.
const buildRxCols = (openPickup) => [
  { title:"Medication",   dataIndex:"medication_name", key:"medication_name" },
  { title:"Dosage",       dataIndex:"dosage",          key:"dosage",        width:120 },
  { title:"Prescribed By",key:"prescribed_by",
    render:(_,r)=><Text>{r.doctor_name ?? r.doctor_email ?? "—"}</Text> },
  { title:"Pickup",       key:"pickup",                width:200,
    render:(_,r)=>{
      const auth = r.pickup_authorization_type;
      if (auth === "PARTNER_ORG" && r.pickup_partner_org_name)
        return <Tag color="purple">{r.pickup_partner_org_name} ({r.pickup_partner_org_code})</Tag>;
      if (auth === "PERSONAL_PROXY" && r.pickup_proxy_name)
        return <Tag color="cyan">{r.pickup_proxy_name}</Tag>;
      return <Tag color="default">Self</Tag>;
    } },
  { title:"Date",         dataIndex:"created_at",      key:"created_at",    width:115, render:fmtDate },
  { title:"Status",       dataIndex:"is_dispensed",    key:"is_dispensed",  width:110,
    render:v=>v?<Tag color="green">Dispensed</Tag>:<Tag color="orange">Pending</Tag> },
  { title:"",             key:"actions",               width:100,
    render:(_,r)=>!r.is_dispensed && (
      <Button size="small" onClick={()=>openPickup(r)}>Set Pickup</Button>
    ) },
];

const EMS_COLS = [
  { title:"Address",    dataIndex:"pickup_address",  key:"pickup_address", ellipsis:true },
  { title:"Reason",     dataIndex:"reason",          key:"reason",         ellipsis:true },
  { title:"Requested",  dataIndex:"requested_at",    key:"requested_at",   width:130, render:fmtDate },
  { title:"Status",     dataIndex:"status",          key:"status",         width:110,
    render:s=><Tag color={EMS_COLOR[s]??"default"}>{s??"—"}</Tag> },
];

const OPTICAL_COLS = [
  { title:"Exam Date",       dataIndex:"examination_date",   key:"examination_date",   width:120, render:fmtDate },
  { title:"Right Eye Vision",dataIndex:"right_eye_vision",   key:"right_eye_vision",   render:v=>v??"—" },
  { title:"Left Eye Vision", dataIndex:"left_eye_vision",    key:"left_eye_vision",    render:v=>v??"—" },
  { title:"Diagnosis",       dataIndex:"diagnosis",          key:"diagnosis",          ellipsis:true },
  { title:"Recommendations", dataIndex:"recommendations",    key:"recommendations",    ellipsis:true },
];

const BILLING_COLS = [ // eslint-disable-line no-unused-vars
  { title:"Service Date",  dataIndex:"service_date",          key:"service_date",          width:120, render:fmtDate },
  { title:"Provider",      dataIndex:"service_provider_email",key:"service_provider_email",render:e=>e??"—" },
  { title:"Amount Billed", dataIndex:"amount_billed",         key:"amount_billed",         width:130, render:a=>a!=null?`₱${parseFloat(a).toFixed(2)}`:"—" },
  { title:"Payor",         dataIndex:"payor_type",            key:"payor_type",            width:100, render:t=>t?<Tag>{t}</Tag>:"—" },
  { title:"Status",        dataIndex:"submission_status",     key:"submission_status",     width:110,
    render:s=><Tag color={BILLING_COLOR[s]??"default"}>{s??"—"}</Tag> },
];

const PROXY_COLS = [
  { title:"Proxy Email", dataIndex:"proxy_email", key:"proxy_email", render:e=>e??"—" },
  { title:"Status",      dataIndex:"is_active",   key:"is_active",   width:90,
    render:v=>v?<Tag color="green">Active</Tag>:<Tag>Inactive</Tag> },
  { title:"Added",       dataIndex:"created_at",  key:"created_at",  width:120, render:fmtDate },
];

export default function PatientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { hasModule } = useModules();

  const [collapsed,    setCollapsed]    = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [retryCount,   setRetryCount]   = useState(0);

  const [patientProfile, setPatientProfile] = useState(null);
  const [prescriptions,  setPrescriptions]  = useState([]);
  const [billingRows,    setBillingRows]    = useState([]); // eslint-disable-line no-unused-vars
  const [emsRows,        setEmsRows]        = useState([]);
  const [proxyRows,      setProxyRows]      = useState([]);
  const [opticalRows,    setOpticalRows]    = useState([]);
  const [stats, setStats] = useState({ activePrescriptions:0, billingRecords:0, emsRequests:0, proxyUsers:0 });

  // ── medical history state ──────────────────────────────────────────────────────
  const [myDiagnoses,         setMyDiagnoses]         = useState([]);
  const [medHistoryLoaded,    setMedHistoryLoaded]    = useState(false);
  const [medHistoryLoading,   setMedHistoryLoading]   = useState(false);

  // ── appointment state ─────────────────────────────────────────────────────
  const [appointments,       setAppointments]       = useState([]);
  const [apptLoading,        setApptLoading]        = useState(false);
  const [bookingModal,       setBookingModal]       = useState(false);
  const [availableDoctors,   setAvailableDoctors]   = useState([]);
  const [bookingForm]                               = Form.useForm();

  const [emsModal,           setEmsModal]           = useState(false);
  const [proxyModal,         setProxyModal]         = useState(false);
  const [insuranceModal,     setInsuranceModal]     = useState(false); // eslint-disable-line no-unused-vars
  const [submitting,         setSubmitting]         = useState(false);
  const [pharmacyList,       setPharmacyList]       = useState([]);
  const [savingPreferences,  setSavingPreferences]  = useState(false);
  const [preferredPharmacy,  setPreferredPharmacy]  = useState(null);

  const [emsForm]   = Form.useForm();
  const [proxyForm] = Form.useForm();

  // pickup authorization
  const [pickupModal,       setPickupModal]       = useState(false);
  const [pickupRx,          setPickupRx]          = useState(null);
  const [pickupForm]                              = Form.useForm();
  const [pickupAuthType,    setPickupAuthType]    = useState("SELF");
  const [pickupSubmitting,  setPickupSubmitting]  = useState(false);
  const [partnerOrgs,       setPartnerOrgs]       = useState([]);
  // watches the partner org field value to drive the visual selection state in the partner card
  const watchedPartnerId = Form.useWatch("pickup_partner_org_id", pickupForm);
  const watchedPharmacyId = Form.useWatch("preferred_pharmacy_id", pickupForm);

  // per-prescription available pharmacy list (loaded lazily when modal opens)
  const [rxPharmacies,        setRxPharmacies]        = useState({});
  const [rxPharmaciesLoading, setRxPharmaciesLoading] = useState(false);

  // support
  const [myTickets,      setMyTickets]      = useState([]);
  const [ticketModal,    setTicketModal]    = useState(false);
  const [ticketDetail,   setTicketDetail]   = useState(null);
  const [supportLoaded,  setSupportLoaded]  = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [ticketForm]                        = Form.useForm();

  // share records
  const [shareConsents,      setShareConsents]      = useState([]);
  const [shareLoaded,        setShareLoaded]         = useState(false);
  const [shareLoading,       setShareLoading]        = useState(false);
  const [shareModal,         setShareModal]          = useState(false);
  const [shareType,          setShareType]           = useState("HOSPITAL");
  const [networkHospitals,   setNetworkHospitals]   = useState([]);
  const [networkDoctors,     setNetworkDoctors]      = useState([]);
  const [shareForm]                                  = Form.useForm();
  const [shareSubmitting,    setShareSubmitting]     = useState(false);

  const fetchProxies = useCallback(async () => {
    const res = await getProxyUsers();
    const rows = toArray(res.data);
    setProxyRows(rows.map(r=>({...r,key:r.id})));
    return rows;
  }, []);

  const fetchEms = useCallback(async () => {
    const res = await getEMSRequests();
    const rows = toArray(res.data);
    setEmsRows(rows.map(r=>({...r,key:r.id})));
    return rows;
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const [profRes, rxRes, billRes, emsRes, proxyRes, optRes, pharmRes, apptRes] = await Promise.all([
          getPatientProfile(), getPrescriptions(), getBillingRecords(),
          getEMSRequests(),    getProxyUsers(),    getOpticalServices(),
          getAllPharmacies().catch(() => ({ data: [] })),
          getPatientAppointments().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const rx      = toArray(rxRes.data);
        const bill    = toArray(billRes.data);
        const ems     = toArray(emsRes.data);
        const proxies = toArray(proxyRes.data);
        const optical = toArray(optRes.data);
        const pharms  = toArray(pharmRes.data);
        const appts   = toArray(apptRes.data);
        const profile = Array.isArray(profRes.data) ? profRes.data[0]
          : (profRes.data?.results?.[0] ?? profRes.data ?? null);
        setPatientProfile(profile);
        setPharmacyList(pharms);
        setPreferredPharmacy(profile?.preferred_pharmacy ?? null);
        setPrescriptions(rx.map(r=>({...r,key:r.id})));
        setBillingRows(bill.map(r=>({...r,key:r.id})));
        setEmsRows(ems.map(r=>({...r,key:r.id})));
        setProxyRows(proxies.map(r=>({...r,key:r.id})));
        setOpticalRows(optical.map(r=>({...r,key:r.id})));
        setAppointments(appts.map(r=>({...r,key:r.id})));
        setStats({
          activePrescriptions: rx.filter(r=>r.is_active&&!r.is_dispensed).length,
          billingRecords:      bill.length,
          emsRequests:         ems.length,
          proxyUsers:          proxies.length,
        });
      } catch {
        if (!cancelled) setError("Failed to load dashboard data. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── medical history lazy load ─────────────────────────────────────────────────
  useEffect(() => {
    if (selectedMenu !== "medical-history" || medHistoryLoaded) return;
    let cancelled = false;
    async function load() {
      setMedHistoryLoading(true);
      try {
        const dxRes = await getMyDiagnoses();
        if (cancelled) return;
        setMyDiagnoses(toArray(dxRes.data).map((r, i) => ({ ...r, key: r.id ?? i })));
        setMedHistoryLoaded(true);
      } catch {
        // silently fail — medical history is non-critical
      } finally {
        if (!cancelled) setMedHistoryLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedMenu, medHistoryLoaded]);

  // ── appointments section: load available doctors + full list on first open ───
  const fetchAppointments = useCallback(async () => {
    setApptLoading(true);
    try {
      const [apptRes, docRes] = await Promise.all([
        getPatientAppointments(),
        getAvailableDoctors().catch(() => ({ data: [] })),
      ]);
      setAppointments(toArray(apptRes.data).map((r, i) => ({ ...r, key: r.id ?? i })));
      setAvailableDoctors(toArray(docRes.data));
    } catch { /* silent */ } finally {
      setApptLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu !== "appointments") return;
    fetchAppointments();
  }, [selectedMenu, fetchAppointments]);

  const handleBookAppointment = async (values) => {
    setSubmitting(true);
    try {
      await requestAppointment({
        doctor_id:      values.doctor_id,
        preferred_date: dayjs(values.preferred_date).format("YYYY-MM-DD"),
        preferred_time: dayjs(values.preferred_time).format("HH:mm"),
        reason:         values.reason ?? "",
        patient_notes:  values.patient_notes ?? "",
      });
      message.success("Appointment request sent. Waiting for doctor approval.");
      setBookingModal(false);
      bookingForm.resetFields();
      fetchAppointments();
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.detail ?? d?.doctor_id?.[0] ?? "Failed to request appointment.");
    } finally { setSubmitting(false); }
  };

  const handleCancelAppointment = async (appt) => {
    try {
      await cancelAppointment(appt.id);
      message.success("Appointment cancelled.");
      fetchAppointments();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to cancel appointment.");
    }
  };

  // ── handlers ─────────────────────────────────────────────────────────────────
  const handleEMS = async (values) => {
    setSubmitting(true);
    try {
      await requestEMS({
        pickup_address:       values.pickup_address,
        reason:               values.reason,
        destination_hospital: values.destination_hospital ?? "",
        notes:                values.notes ?? "",
      });
      message.success("EMS request submitted. Help is on the way.");
      setEmsModal(false); emsForm.resetFields();
      const rows = await fetchEms();
      setStats(s=>({...s, emsRequests:rows.length}));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.pickup_address?.[0] ?? d?.reason?.[0] ?? d?.detail ?? "Failed to submit EMS request.");
    } finally { setSubmitting(false); }
  };

  const handleAddProxy = async (values) => {
    setSubmitting(true);
    try {
      await addProxyUser({ proxy: values.proxyUserEmail, is_active: true });
      message.success("Proxy user added successfully.");
      setProxyModal(false); proxyForm.resetFields();
      const rows = await fetchProxies();
      setStats(s=>({...s, proxyUsers:rows.length}));
    } catch (err) {
      const d = err?.response?.data;
      message.error(d?.proxy?.[0] ?? d?.non_field_errors?.[0] ?? d?.detail ?? "Failed to add proxy user.");
    } finally { setSubmitting(false); }
  };

  // load partner orgs lazily when pickup modal opens
  const ensurePartnerOrgs = useCallback(async () => {
    if (partnerOrgs.length > 0) return;
    try {
      const res = await getPublicPartnerOrgs();
      setPartnerOrgs(Array.isArray(res.data) ? res.data : (res.data?.results ?? []));
    } catch { /* silent */ }
  }, [partnerOrgs.length]);

  const openPickupModal = async (rx) => {
    setPickupRx(rx);
    setPickupAuthType(rx.pickup_authorization_type ?? "SELF");
    pickupForm.setFieldsValue({
      pickup_authorization_type: rx.pickup_authorization_type ?? "SELF",
      pickup_partner_org_id:     rx.pickup_partner_org ?? undefined,
      pickup_proxy_name:         rx.pickup_proxy_name  ?? "",
      pickup_proxy_id:           rx.pickup_proxy_id    ?? "",
      preferred_pharmacy_id:     rx.preferred_pharmacy ?? undefined,
    });
    ensurePartnerOrgs();
    setPickupModal(true);

    // Lazy-load available pharmacies for this prescription
    if (!rxPharmacies[rx.id]) {
      setRxPharmaciesLoading(true);
      try {
        const res = await getAvailablePharmacies(rx.id);
        setRxPharmacies(prev => ({ ...prev, [rx.id]: toArray(res.data) }));
      } catch { /* silent — fallback to plain list */ } finally {
        setRxPharmaciesLoading(false);
      }
    }
  };

  // Updates both the display state and the hidden form field when a pickup card is clicked.
  // Keeps all submission logic in handlePickupSave unchanged.
  const handleCardSelect = (value) => {
    setPickupAuthType(value);
    pickupForm.setFieldsValue({ pickup_authorization_type: value });
    if (value === "PARTNER_ORG" && partnerOrgs.length === 1) {
      pickupForm.setFieldsValue({ pickup_partner_org_id: partnerOrgs[0].id });
    }
  };

  const handlePickupSave = async (values) => {
    if (!pickupRx) return;
    setPickupSubmitting(true);
    try {
      const payload = { pickup_authorization_type: values.pickup_authorization_type };
      if (values.pickup_authorization_type === "PARTNER_ORG") {
        payload.pickup_partner_org_id = values.pickup_partner_org_id;
      } else if (values.pickup_authorization_type === "PERSONAL_PROXY") {
        payload.pickup_proxy_name = values.pickup_proxy_name;
        payload.pickup_proxy_id   = values.pickup_proxy_id;
      }
      if (values.preferred_pharmacy_id) {
        payload.preferred_pharmacy_id = values.preferred_pharmacy_id;
      }
      await updatePrescriptionPickup(pickupRx.id, payload);
      message.success("Pickup authorisation updated.");
      setPickupModal(false);
      // Refresh prescriptions
      const rxRes = await getPrescriptions();
      const rx = Array.isArray(rxRes.data) ? rxRes.data : (rxRes.data?.results ?? []);
      setPrescriptions(rx.map(r => ({ ...r, key: r.id })));
    } catch (err) {
      const d = err?.response?.data;
      const msg = d?.pickup_partner_org_id?.[0] ?? d?.pickup_proxy_name?.[0] ??
        d?.pickup_proxy_id?.[0] ?? d?.detail ?? "Failed to update pickup authorisation.";
      message.error(msg);
    } finally { setPickupSubmitting(false); }
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const userMenuItems = [{ key:"logout", icon:<LogoutOutlined />, label:"Logout", danger:true, onClick:handleLogout }];

  // ── support handlers ──────────────────────────────────────────────────────────
  const loadSupport = useCallback(async () => {
    setSupportLoading(true);
    try {
      const res = await getMyTickets();
      setMyTickets(toArray(res.data).map(r=>({...r,key:r.id})));
      setSupportLoaded(true);
    } catch { /* silent */ } finally { setSupportLoading(false); }
  }, []);

  useEffect(() => {
    if (selectedMenu === "support" && !supportLoaded) loadSupport();
  }, [selectedMenu, supportLoaded, loadSupport]);

  const loadShareConsents = useCallback(async () => {
    setShareLoading(true);
    try {
      const [consRes, hospRes, docRes] = await Promise.all([
        getShareConsents(),
        getNetworkHospitals().catch(() => ({ data: [] })),
        getNetworkDoctors().catch(() => ({ data: [] })),
      ]);
      setShareConsents(toArray(consRes.data).map(r => ({ ...r, key: r.id })));
      setNetworkHospitals(toArray(hospRes.data));
      setNetworkDoctors(toArray(docRes.data));
      setShareLoaded(true);
    } catch { /* silent */ } finally { setShareLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMenu === "share-records" && !shareLoaded) loadShareConsents();
  }, [selectedMenu, shareLoaded, loadShareConsents]);

  const handleShareRecords = async (values) => {
    setShareSubmitting(true);
    try {
      await shareRecords({ share_type: shareType, target_id: values.target_id, note: values.note ?? "" });
      message.success("Records shared successfully.");
      setShareModal(false);
      shareForm.resetFields();
      loadShareConsents();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to share records.");
    } finally { setShareSubmitting(false); }
  };

  const handleRevokeShare = async (consent) => {
    try {
      await revokeShareConsent(consent.id);
      message.success("Access revoked.");
      setShareConsents(prev => prev.map(c => c.id === consent.id ? { ...c, status: "REVOKED" } : c));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to revoke access.");
    }
  };

  const handleCreateTicket = async (values) => {
    try {
      await createTicket(values);
      message.success("Ticket submitted successfully.");
      setTicketModal(false);
      ticketForm.resetFields();
      loadSupport();
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to submit ticket.");
    }
  };

  const handleSavePreferredPharmacy = async () => {
    if (!patientProfile?.id) { message.error("Profile not loaded."); return; }
    setSavingPreferences(true);
    try {
      await updatePatientProfile(patientProfile.id, { preferred_pharmacy: preferredPharmacy ?? "" });
      message.success("Preferred pharmacy saved.");
      setPatientProfile(p => ({ ...p, preferred_pharmacy: preferredPharmacy }));
    } catch (err) {
      message.error(err?.response?.data?.detail ?? "Failed to save preferred pharmacy.");
    } finally {
      setSavingPreferences(false);
    }
  };

  const upcomingAppts = appointments.filter(a => {
    if (!["PENDING_APPROVAL", "CONFIRMED"].includes(a.status)) return false;
    // Use whichever date field is set — PENDING_APPROVAL has preferred_date, CONFIRMED has scheduled_at
    const dateStr = a.scheduled_at || a.preferred_date;
    if (!dateStr) return true; // no date yet, count it
    return dayjs(dateStr).isAfter(dayjs().subtract(1, "day"));
  }).length;

  const statCards = [
    { title:"Active Prescriptions", value:stats.activePrescriptions, color:"#722ed1" },
    ...(hasModule("DOCTOR") ? [{ title:"Upcoming Appointments", value:upcomingAppts, color:"#1677ff" }] : []),
    ...(hasModule("EMS") ? [{ title:"EMS Requests", value:stats.emsRequests, color:"#f5222d" }] : []),
  ];

  const rxCols = buildRxCols(openPickupModal);

  // ── views ─────────────────────────────────────────────────────────────────────
  function renderContent() {
    switch (selectedMenu) {
      case "dashboard":
        return (
          <>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Patient Dashboard</Title>
            <Alert type="info" showIcon message="Welcome to MCHUSA Digital Healthcare Network. Your health, our priority."
              style={{marginBottom:24,borderRadius:8}} />
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              {statCards.map(({title,value,color})=>(
                <Col xs={24} sm={12} lg={6} key={title}>
                  <Card variant="borderless" style={{borderRadius:8}}>
                    <Statistic title={<Text style={{color:"#8c8c8c",fontSize:13}}>{title}</Text>}
                      value={value} styles={{ content: {color,fontSize:28,fontWeight:600} }} />
                  </Card>
                </Col>
              ))}
            </Row>
            <Row gutter={[16,16]} style={{marginBottom:24}}>
              <Col xs={24}>
                <Card title={<Text strong>My Prescriptions</Text>} variant="borderless" style={{borderRadius:8}}>
                  <Table columns={rxCols} dataSource={prescriptions}
                    pagination={{pageSize:6,hideOnSinglePage:true}}
                    locale={{emptyText:"No prescriptions yet"}} size="small" />
                </Card>
              </Col>
            </Row>
            {hasModule("EMS") && (
              <Row gutter={[16,16]}>
                <Col xs={24}>
                  <Card title={<Text strong>Quick Actions</Text>} variant="borderless" style={{borderRadius:8}}>
                    <Row gutter={12}>
                      <Col><Button danger type="primary" icon={<AlertOutlined />} onClick={()=>setEmsModal(true)}>Request EMS</Button></Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        );

      case "prescriptions":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>My Prescriptions</Title>
            <Card variant="borderless" style={{borderRadius:8}}>
              <Table columns={rxCols} dataSource={prescriptions}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No prescriptions yet"}} size="small" />
            </Card>
          </>
        );

      case "medical-history": {
        const SEV_COLOR = { MILD:"green", MODERATE:"orange", SEVERE:"red", CRITICAL:"volcano" };

        const dxCols = [
          { title:"ICD-10",    dataIndex:"icd10_code",        key:"icd10_code",        width:90 },
          { title:"Condition", dataIndex:"icd10_description", key:"icd10_description", ellipsis:true },
          { title:"Doctor",    dataIndex:"doctor_email",      key:"doctor_email",       render:e=>e??"—" },
          { title:"Date",      dataIndex:"diagnosis_date",    key:"diagnosis_date",    width:110, render:fmtDate },
          { title:"Severity",  dataIndex:"severity",          key:"severity",          width:100,
            render:s=>s?<Tag color={SEV_COLOR[s]??"default"}>{s}</Tag>:"—" },
          { title:"Status",    dataIndex:"is_active",         key:"is_active",         width:90,
            render:v=>v?<Tag color="blue">Active</Tag>:<Tag>Resolved</Tag> },
        ];

        // Timeline: merge diagnoses + prescriptions
        const timelineItems = [
          ...myDiagnoses.map(d=>({ date:d.diagnosis_date, type:"diagnosis",
            label:`[Dx] ${d.icd10_code} — ${d.icd10_description}` })),
          ...prescriptions.map(p=>({ date:p.created_at?.slice(0,10), type:"prescription",
            label:`[Rx] ${p.medication_name??"—"} · ${p.dosage??"—"}` })),
        ].sort((a,b)=>(b.date??"").localeCompare(a.date??""));

        return (
          <Spin spinning={medHistoryLoading}>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Medical History</Title>

            {/* My Diagnoses */}
            <Card variant="borderless" style={{borderRadius:8,marginBottom:16}}
              title={<Text strong>My Diagnoses</Text>}>
              <Table columns={dxCols} dataSource={myDiagnoses}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No diagnoses recorded yet"}} size="small" />
            </Card>

            {/* Consent notice — sharing is authorized offline via paper form */}
            <Alert type="info" showIcon style={{marginBottom:16,borderRadius:8}}
              message="Consent on file"
              description="You signed a paper consent form authorizing doctors on the MCHUSA network to view your medical history for consultation. Every access is recorded in our audit log." />

            {/* Medical Timeline */}
            {timelineItems.length > 0 && (
              <Card variant="borderless" style={{borderRadius:8}}
                title={<Text strong>Medical Timeline</Text>}>
                <Timeline
                  items={timelineItems.map(item=>({
                    color: item.type==="diagnosis" ? "red" : "blue",
                    children:(
                      <span>
                        <Text type="secondary" style={{fontSize:12}}>{item.date??"—"}</Text>
                        <br />
                        <Text>{item.label}</Text>
                      </span>
                    ),
                  }))}
                />
              </Card>
            )}
          </Spin>
        );
      }

      case "ems":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>EMS Services</Title>
            <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>My EMS Requests</Text>}
              extra={<Button danger type="primary" size="small" icon={<AlertOutlined />} onClick={()=>setEmsModal(true)}>Request EMS</Button>}>
              <Table columns={EMS_COLS} dataSource={emsRows}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No EMS requests"}} size="small" />
            </Card>
          </>
        );

      case "optical":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Optical Services</Title>
            <Card variant="borderless" style={{borderRadius:8}}>
              <Table columns={OPTICAL_COLS} dataSource={opticalRows}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No eye examinations on record"}} size="small" />
            </Card>
          </>
        );


      case "appointments": {
        const APPT_COLOR = {
          PENDING_APPROVAL: "gold",
          CONFIRMED:        "blue",
          COMPLETED:        "green",
          CANCELLED:        "default",
          REJECTED:         "red",
        };
        const APPT_LABEL = {
          PENDING_APPROVAL: "Awaiting Approval",
          CONFIRMED:        "Confirmed",
          COMPLETED:        "Completed",
          CANCELLED:        "Cancelled",
          REJECTED:         "Rejected",
        };
        const apptCols = [
          { title:"When", key:"when", width:200,
            render:r => r.status === "CONFIRMED" && r.scheduled_at
              ? <span><Text type="secondary" style={{fontSize:11}}>Confirmed:</Text><br /><Text strong>{dayjs(r.scheduled_at).format("MMM D, YYYY · h:mm A")}</Text></span>
              : r.preferred_date
                ? <span><Text type="secondary" style={{fontSize:11}}>Requested:</Text><br />{r.preferred_date}{r.preferred_time ? ` at ${r.preferred_time.slice(0,5)}` : ""}</span>
                : "—" },
          { title:"Doctor",    dataIndex:"doctor_email",    key:"doctor_email",    render:e=>e??"—" },
          { title:"Specialty", dataIndex:"doctor_specialty",key:"doctor_specialty",render:s=>s??"—",width:130 },
          { title:"Duration",  dataIndex:"duration_minutes",key:"duration_minutes",width:90,
            render:v=>v ? `${v} min` : <Text type="secondary">TBD</Text> },
          { title:"Reason",    dataIndex:"reason",          key:"reason",          ellipsis:true,
            render:v=>v||<Text type="secondary">—</Text> },
          { title:"Status",    dataIndex:"status",          key:"status",          width:160,
            render:s=><Tag color={APPT_COLOR[s]??"default"}>{APPT_LABEL[s]??s}</Tag> },
          { title:"Actions",   key:"actions",               width:110,
            render:(_,r)=> ["PENDING_APPROVAL","CONFIRMED"].includes(r.status) ? (
              <Popconfirm title="Cancel this appointment request?" okText="Cancel It" okButtonProps={{danger:true}}
                onConfirm={()=>handleCancelAppointment(r)}>
                <Button size="small" danger>Cancel</Button>
              </Popconfirm>
            ) : null },
        ];
        return (
          <Spin spinning={apptLoading}>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>My Appointments</Title>
            <Card variant="borderless" style={{borderRadius:8}}
              title={<Text strong>Appointment Requests</Text>}
              extra={
                <Button type="primary" size="small" icon={<CalendarOutlined />}
                  onClick={()=>setBookingModal(true)}>
                  Book Appointment
                </Button>
              }>
              <Table columns={apptCols} dataSource={appointments}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No appointments yet. Click 'Book Appointment' to request one."}}
                size="small" />
            </Card>
          </Spin>
        );
      }

      case "proxies":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>Proxy Users</Title>
            <Card variant="borderless" style={{borderRadius:8}} title={<Text strong>Authorized Proxies</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setProxyModal(true)}>Add Proxy</Button>}>
              <Table columns={PROXY_COLS} dataSource={proxyRows}
                pagination={{pageSize:15,hideOnSinglePage:true}}
                locale={{emptyText:"No proxy users added yet"}} size="small" />
            </Card>
          </>
        );

      case "profile":
        return (
          <>
            <Title level={4} style={{marginBottom:24,color:"#262626"}}>My Profile</Title>
            <Card variant="borderless" style={{borderRadius:8,marginBottom:16}}>
              {patientProfile ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Email" span={2}>{patientProfile.user_email ?? user.email}</Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">{patientProfile.date_of_birth ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Patient PIN">{patientProfile.pin ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Insurance Carrier">{patientProfile.insurance_carrier ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Policy Number">{patientProfile.insurance_policy_number ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Emergency Contact">{patientProfile.emergency_contact_name ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Emergency Phone">{patientProfile.emergency_contact_phone ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Address" span={2}>{patientProfile.address ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="ID Type">{patientProfile.government_id_type || "—"}</Descriptions.Item>
                  <Descriptions.Item label="ID Status">
                    {patientProfile.government_id_verified
                      ? <Tag color="green">Verified</Tag>
                      : patientProfile.government_id_number
                        ? <Tag color="orange">Pending Verification</Tag>
                        : <Tag>No ID on File — contact your doctor</Tag>}
                  </Descriptions.Item>
                  {patientProfile.government_id_photo_url && (
                    <Descriptions.Item label="Government ID Photo" span={2}>
                      <img
                        src={patientProfile.government_id_photo_url}
                        alt="Government ID"
                        style={{ maxWidth: 220, maxHeight: 160, borderRadius: 4, objectFit: "cover", border: "1px solid #d9d9d9" }}
                      />
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ) : (
                <Text type="secondary">No profile found. Please contact your administrator.</Text>
              )}
            </Card>

            {patientProfile && (
              <Card variant="borderless" style={{borderRadius:8}}
                title={<Text strong>Preferred Pharmacy</Text>}>
                <Alert type="info" showIcon style={{marginBottom:16}}
                  message="Your preferred pharmacy will be pre-selected when a doctor issues you a prescription. You can still pick up from any pharmacy." />
                <Row gutter={12} align="middle">
                  <Col flex="auto">
                    <Select
                      style={{width:"100%"}}
                      placeholder="Select your preferred pharmacy"
                      value={preferredPharmacy || undefined}
                      onChange={setPreferredPharmacy}
                      allowClear
                      showSearch
                      optionFilterProp="children">
                      {pharmacyList.map(ph=>(
                        <Select.Option key={ph.id} value={ph.id}>
                          {ph.pharmacy_name}{ph.address ? ` — ${ph.address}` : ""}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col>
                    <Button type="primary" loading={savingPreferences}
                      onClick={handleSavePreferredPharmacy}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}
            <ChangePasswordForm />
          </>
        );

      case "support":
        return (
          <Spin spinning={supportLoading}>
            <Title level={4} style={{marginBottom:16,color:"#262626"}}>Support</Title>
            <Card variant="borderless" style={{borderRadius:8,marginBottom:16}} title={<Text strong>My Tickets</Text>}
              extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={()=>setTicketModal(true)}>Report an Issue</Button>}>
              <Table
                size="small"
                dataSource={myTickets}
                pagination={{pageSize:10,hideOnSinglePage:true}}
                locale={{emptyText:"No tickets yet"}}
                columns={[
                  { title:"ID",       dataIndex:"id",       key:"id",       width:90, render:id=><Text code style={{fontSize:11}}>{String(id).slice(0,8)}</Text> },
                  { title:"Subject",  dataIndex:"subject",  key:"subject",  ellipsis:true },
                  { title:"Priority", dataIndex:"priority", key:"priority", width:100, render:p=><Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[p]??"default"}>{p}</Tag> },
                  { title:"Status",   dataIndex:"status",   key:"status",   width:110, render:s=><Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[s]??"default"}>{s?.replace(/_/g," ")}</Tag> },
                  { title:"Created",  dataIndex:"created_at",key:"created_at",width:110, render:fmtDate },
                  { title:"Actions",  key:"actions",        width:80, render:(_,r)=><Button size="small" onClick={()=>setTicketDetail(r)}>View</Button> },
                ]}
              />
            </Card>
          </Spin>
        );

      case "share-records": {
        const shareCols = [
          { title: "Shared With", key: "target_name", render: (_, r) => <Text strong>{r.target_name ?? "—"}</Text> },
          { title: "Type", dataIndex: "share_type", key: "share_type", width: 120,
            render: t => <Tag color={t === "HOSPITAL" ? "blue" : t === "DOCTOR" ? "purple" : "gold"}>{t}</Tag> },
          { title: "Shared", dataIndex: "shared_at", key: "shared_at", width: 130, render: fmtDate },
          { title: "Status", dataIndex: "status", key: "status", width: 90,
            render: s => s === "ACTIVE"
              ? <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
              : <Tag color="default" icon={<StopOutlined />}>Revoked</Tag> },
          { title: "Actions", key: "actions", width: 130,
            render: (_, r) => r.status === "ACTIVE" ? (
              <Popconfirm
                title={`Revoke access for ${r.target_name}?`}
                description="They will no longer be able to view your medical records."
                onConfirm={() => handleRevokeShare(r)}
                okText="Revoke" okButtonProps={{ danger: true }} cancelText="Cancel"
              >
                <Button danger size="small" icon={<StopOutlined />}>Revoke</Button>
              </Popconfirm>
            ) : <Text type="secondary" style={{ fontSize: 12 }}>Revoked</Text> },
        ];

        return (
          <Spin spinning={shareLoading}>
            <Title level={4} style={{ marginBottom: 16, color: "#262626" }}>Share My Records</Title>

            <Alert type="info" showIcon style={{ marginBottom: 20, borderRadius: 8 }}
              message="How Medical Record Sharing Works"
              description={
                <ul style={{ marginBottom: 0, paddingLeft: 18, marginTop: 4, lineHeight: 1.8 }}>
                  <li>Your medical records are private and <strong>controlled by you</strong>.</li>
                  <li><strong>Share with a Hospital</strong> — all doctors at that hospital can view your history during your visit.</li>
                  <li><strong>Share with a Doctor</strong> — only that specific doctor can view your records.</li>
                  <li>You can <strong>revoke access at any time</strong>.</li>
                  <li>Any treatments added by external doctors appear in your full history, attributed to them.</li>
                </ul>
              }
            />

            <Card variant="borderless" style={{ borderRadius: 8, marginBottom: 16 }}
              title={<Text strong>Currently Shared With</Text>}
              extra={
                <Button type="primary" icon={<ShareAltOutlined />} onClick={() => setShareModal(true)}>
                  Share My Records
                </Button>
              }
            >
              <Table columns={shareCols} dataSource={shareConsents}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: "You haven't shared your records with anyone yet." }}
                size="small" />
            </Card>

            {/* Share Records Modal */}
            <Modal
              title="Share Your Medical Records"
              open={shareModal}
              onCancel={() => { setShareModal(false); shareForm.resetFields(); setShareType("HOSPITAL"); }}
              footer={null}
              destroyOnClose
            >
              <Form form={shareForm} layout="vertical" onFinish={handleShareRecords}>
                <Form.Item label="Who would you like to share with?">
                  <Select value={shareType} onChange={v => { setShareType(v); shareForm.resetFields(["target_id"]); }}>
                    <Select.Option value="HOSPITAL">A Hospital (recommended)</Select.Option>
                    <Select.Option value="DOCTOR">A Specific Doctor</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item name="target_id" label={shareType === "HOSPITAL" ? "Select Hospital" : "Select Doctor"}
                  rules={[{ required: true, message: "Please select a recipient." }]}>
                  <Select showSearch placeholder={shareType === "HOSPITAL" ? "Search hospitals…" : "Search doctors…"}
                    filterOption={(input, opt) => (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    options={shareType === "HOSPITAL"
                      ? networkHospitals.map(h => ({ value: h.id, label: `${h.name} — ${h.organisation_name}` }))
                      : networkDoctors.map(d => ({
                          value: d.profile_id,
                          label: `Dr. ${d.first_name} ${d.last_name} — ${d.specialty}${d.hospital_name ? ` @ ${d.hospital_name}` : ""}`,
                        }))
                    }
                  />
                </Form.Item>

                <Form.Item name="note" label="Note (optional)">
                  <Input.TextArea rows={2} placeholder="Add a note (e.g. upcoming visit, reason for sharing)" />
                </Form.Item>

                <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                  message="Your records will only be viewable by the selected hospital or doctor. You can revoke this at any time." />

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Button onClick={() => setShareModal(false)}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={shareSubmitting} icon={<ShareAltOutlined />}>
                    Share Records
                  </Button>
                </div>
              </Form>
            </Modal>
          </Spin>
        );
      }

      default: return null;
    }
  }

  // ── render ────────────────────────────────────────────────────────────────────
  const pendingApptCount = appointments.filter(a => a.status === "PENDING_APPROVAL").length;

  const menuItems = [
    { key:"dashboard",       icon:<DashboardOutlined />,        label:"Dashboard" },
    ...(hasModule("DOCTOR") || hasModule("PHARMACY")
      ? [{ key:"prescriptions", icon:<MedicineBoxOutlined />,   label:"My Prescriptions" }]
      : []),
    { key:"medical-history", icon:<FileTextOutlined />,         label:"Medical History" },
    ...(hasModule("DOCTOR")
      ? [{
          key:"appointments",
          icon:<CalendarOutlined />,
          label: pendingApptCount > 0
            ? <span>Appointments <Badge count={pendingApptCount} size="small" style={{marginLeft:4}} /></span>
            : "Appointments",
        }]
      : []),
    ...(hasModule("EMS")
      ? [{ key:"ems",           icon:<AlertOutlined />,         label:"EMS Services" }]
      : []),
    ...(hasModule("OPHTHALMOLOGY")
      ? [{ key:"optical",       icon:<EyeOutlined />,           label:"Optical Services" }]
      : []),
    { key:"share-records",   icon:<ShareAltOutlined />,          label:"Share My Records" },
    { key:"support",         icon:<CustomerServiceOutlined />,   label:"Support" },
    { key:"profile",         icon:<UserOutlined />,              label:"My Profile" },
  ];

  return (
    <Layout style={{minHeight:"100vh"}}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}
        style={{background:"#fff",borderRight:"1px solid #f0f0f0"}}>
        <div style={{height:64,display:"flex",alignItems:"center",justifyContent:"center",
          borderBottom:"1px solid #f0f0f0",padding:"0 16px"}}>
          {!collapsed && (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",lineHeight:1.3}}>
              <Text strong style={{color:"#1677ff",fontSize:13,whiteSpace:"nowrap"}}>Digital Healthcare Network</Text>
              <Text style={{color:"#1677ff",fontSize:12,whiteSpace:"nowrap"}}>Patient</Text>
            </div>
          )}
        </div>
        <Menu mode="inline" selectedKeys={[selectedMenu]} items={menuItems}
          onClick={({key})=>setSelectedMenu(key)} style={{border:"none",marginTop:8}} />
      </Sider>

      <Layout>
        <Header style={{background:"#fff",padding:"0 24px",display:"flex",alignItems:"center",
          justifyContent:"space-between",borderBottom:"1px solid #f0f0f0",position:"sticky",top:0,zIndex:100}}>
          <div>
            <Text strong style={{fontSize:16,color:"#1677ff"}}>MCHUSA</Text>
            <Text style={{fontSize:14,color:"#8c8c8c",marginLeft:8}}>| Digital Healthcare Network</Text>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <PatientNotificationBell />
            <Dropdown menu={{items:userMenuItems}} placement="bottomRight" arrow>
              <div style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <Avatar size={32} icon={<UserOutlined />} style={{background:"#52c41a"}} />
                <Text style={{fontSize:14,color:"#262626"}}>{user.email??"Patient"}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{padding:24,background:"#f5f7fa"}}>
          {loading ? (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {[1, 2, 3, 4].map(i => (
                  <Col xs={24} sm={12} lg={6} key={i}>
                    <Card variant="borderless" style={{ borderRadius: 8 }}>
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </Card>
                  </Col>
                ))}
              </Row>
              <Card variant="borderless" style={{ borderRadius: 8 }}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Card>
            </>
          ) : error ? (
            <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }}
              action={<Button size="small" onClick={() => setRetryCount(c => c + 1)}>Retry</Button>}
              onClose={() => setError(null)} closable />
          ) : renderContent()}
        </Content>
      </Layout>

      {/* ── Book Appointment Modal ── */}
      <Modal
        title="Book an Appointment"
        open={bookingModal}
        onOk={() => bookingForm.submit()}
        onCancel={() => { setBookingModal(false); bookingForm.resetFields(); }}
        okText="Send Request"
        confirmLoading={submitting}
        destroyOnHidden>
        <Form form={bookingForm} layout="vertical" onFinish={handleBookAppointment}>
          <Form.Item name="doctor_id" label="Doctor"
            rules={[{ required: true, message: "Select a doctor" }]}>
            <Select placeholder="Select a doctor" showSearch optionFilterProp="label"
              options={availableDoctors.map(d => ({
                value: d.id,
                label: `${d.email} — ${d.specialty}`,
              }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="preferred_date" label="Preferred Date"
                rules={[{ required: true, message: "Select a date" }]}>
                <DatePicker style={{ width: "100%" }}
                  disabledDate={d => d && d.isBefore(dayjs().startOf("day"))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="preferred_time" label="Preferred Time"
                rules={[{ required: true, message: "Select a time" }]}>
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="reason" label="Reason for Visit">
            <Input.TextArea rows={2} placeholder="Brief description of your concern" />
          </Form.Item>
          <Form.Item name="patient_notes" label="Additional Notes (optional)">
            <Input.TextArea rows={2} placeholder="Any additional information for the doctor" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── New Ticket Modal ── */}
      <Modal title="Report an Issue" open={ticketModal}
        onOk={()=>ticketForm.submit()}
        onCancel={()=>{ setTicketModal(false); ticketForm.resetFields(); }}
        okText="Submit" destroyOnHidden keyboard>
        <Form form={ticketForm} layout="vertical" onFinish={handleCreateTicket}>
          <Form.Item name="subject" label="Subject" rules={[{required:true,message:"Required"}]}>
            <Input placeholder="Brief description of the issue" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{required:true,message:"Select priority"}]}>
            <Select placeholder="Select priority">
              <Select.Option value="LOW">Low</Select.Option>
              <Select.Option value="MEDIUM">Medium</Select.Option>
              <Select.Option value="HIGH">High</Select.Option>
              <Select.Option value="CRITICAL">Critical</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{required:true,message:"Required"}]}>
            <Input.TextArea rows={4} placeholder="Describe the issue in detail" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Ticket Detail Modal ── */}
      <Modal title="Ticket Details" open={Boolean(ticketDetail)}
        onCancel={()=>setTicketDetail(null)}
        footer={<Button onClick={()=>setTicketDetail(null)}>Close</Button>}
        destroyOnHidden keyboard width={500}>
        {ticketDetail && (
          <div>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <tbody>
                {[
                  ["Subject",  ticketDetail.subject],
                  ["Priority", <Tag color={{LOW:"default",MEDIUM:"blue",HIGH:"orange",CRITICAL:"red"}[ticketDetail.priority]}>{ticketDetail.priority}</Tag>],
                  ["Status",   <Tag color={{OPEN:"blue",IN_PROGRESS:"orange",RESOLVED:"green",CLOSED:"default",ESCALATED:"red"}[ticketDetail.status]??"default"}>{ticketDetail.status?.replace(/_/g," ")}</Tag>],
                  ["Created",  new Date(ticketDetail.created_at).toLocaleString()],
                  ["Updated",  new Date(ticketDetail.updated_at).toLocaleString()],
                ].map(([label,val])=>(
                  <tr key={label} style={{borderBottom:"1px solid #f0f0f0"}}>
                    <td style={{padding:"8px 12px",color:"#8c8c8c",width:"35%"}}>{label}</td>
                    <td style={{padding:"8px 12px"}}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{marginTop:12}}>
              <Text strong>Description</Text>
              <div style={{marginTop:6,padding:10,background:"#fafafa",borderRadius:4,fontSize:13,color:"#595959"}}>{ticketDetail.description}</div>
            </div>
            {ticketDetail.status === "ESCALATED" && (
              <div style={{marginTop:12,padding:10,background:"#fff1f0",border:"1px solid #ffa39e",borderRadius:4}}>
                <Text strong style={{color:"#cf1322"}}>Escalated to MCHUSA</Text>
                <div style={{marginTop:4,fontSize:13,color:"#595959"}}>
                  Your ticket has been escalated to MCHUSA for direct resolution. You will be notified once it is resolved.
                </div>
              </div>
            )}
            {ticketDetail.resolution_notes && (
              <div style={{marginTop:12}}>
                <Text strong>Resolution Notes</Text>
                <div style={{marginTop:6,padding:10,background:"#f6ffed",border:"1px solid #b7eb8f",borderRadius:4,fontSize:13}}>{ticketDetail.resolution_notes}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── EMS Modal ── */}
      <Modal
        title={<span style={{color:"#f5222d"}}><AlertOutlined style={{marginRight:8}} />Request Emergency Medical Services</span>}
        open={emsModal} onOk={()=>emsForm.submit()}
        onCancel={()=>{setEmsModal(false);emsForm.resetFields();}}
        okText="Dispatch EMS" okButtonProps={{danger:true}} confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Alert type="warning" showIcon message="This will dispatch emergency services to your location."
          style={{marginBottom:16}} />
        <Form form={emsForm} layout="vertical" onFinish={handleEMS}>
          <Form.Item name="pickup_address" label="Location / Address"
            rules={[{required:true,message:"Enter your current location"}]}>
            <Input.TextArea rows={2} placeholder="House/Unit no., Street, Barangay, City" />
          </Form.Item>
          <Form.Item name="reason" label="Description of Emergency"
            rules={[{required:true,message:"Describe the emergency"}]}>
            <Input.TextArea rows={3} placeholder="Briefly describe the medical emergency" />
          </Form.Item>
          <Form.Item name="destination_hospital" label="Preferred Hospital (optional)">
            <Input placeholder="Leave blank for nearest available" />
          </Form.Item>
          <Form.Item name="notes" label="Additional Notes (optional)">
            <Input.TextArea rows={2} placeholder="Any other relevant information" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Add Proxy Modal ── */}
      <Modal title="Add Proxy User" open={proxyModal} onOk={()=>proxyForm.submit()}
        onCancel={()=>{setProxyModal(false);proxyForm.resetFields();}}
        okText="Add Proxy" confirmLoading={submitting} destroyOnHidden
 keyboard={true}>
        <Alert type="info" showIcon message="A proxy user can act on your behalf."
          style={{marginBottom:16}} />
        <Form form={proxyForm} layout="vertical" onFinish={handleAddProxy}>
          <Form.Item name="proxyUserEmail" label="Proxy User Email"
            rules={[{required:true,message:"Enter the proxy user's email or UUID"}]}>
            <Input placeholder="proxy@example.com or UUID" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Pickup Authorisation Modal ── */}
      <Modal
        title="Set Pickup Authorisation"
        open={pickupModal}
        onOk={() => pickupForm.submit()}
        onCancel={() => { setPickupModal(false); pickupForm.resetFields(); }}
        okText="Save"
        confirmLoading={pickupSubmitting}
        destroyOnHidden
        width={560}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingRight: 2 } }}
      >
        {pickupRx?.is_dispensed ? (
          <Alert type="warning" showIcon
            message="This prescription has been dispensed. Pickup details are locked." />
        ) : (
          <Form form={pickupForm} layout="vertical" onFinish={handlePickupSave}
            initialValues={{ pickup_authorization_type: "SELF" }}>

            {/* Hidden field — holds the selected option value for form submission */}
            <Form.Item name="pickup_authorization_type" style={{ display: "none", margin: 0 }}
              rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            {/* Subtitle — replaces top info banner */}
            <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 20 }}>
              Available at all participating network pharmacies
            </Text>

            {/* Section label */}
            <Text strong style={{ display: "block", fontSize: 13, marginBottom: 12 }}>
              Who will collect this prescription?&ensp;<span style={{ color: "#f5222d", fontWeight: 400 }}>*</span>
            </Text>

            {/* ── Card 1: Self ── */}
            <div style={{
              border: `2px solid ${pickupAuthType === "SELF" ? "#1677ff" : "#E5E7EB"}`,
              background: pickupAuthType === "SELF" ? "#EFF6FF" : "#fff",
              borderRadius: 8, marginBottom: 10, overflow: "hidden",
              transition: "border-color 0.15s, background-color 0.15s",
            }}>
              <div onClick={() => handleCardSelect("SELF")}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", cursor: "pointer" }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 20, flexShrink: 0,
                  background: pickupAuthType === "SELF" ? "#1677ff1a" : "#F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {pickupAuthType === "SELF"
                    ? <CheckCircleFilled style={{ fontSize: 18, color: "#1677ff" }} />
                    : <UserOutlined style={{ fontSize: 16, color: "#6B7280" }} />}
                </div>
                <div>
                  <Text strong style={{ display: "block", fontSize: 14, lineHeight: 1.4,
                    color: pickupAuthType === "SELF" ? "#1677ff" : "#111827" }}>
                    I will collect it myself
                  </Text>
                  <Text style={{ fontSize: 12, color: "#6B7280" }}>
                    Present your DHN PIN at any network pharmacy
                  </Text>
                </div>
              </div>
            </div>

            {/* ── Card 2: Personal Proxy ── */}
            <div style={{
              border: `2px solid ${pickupAuthType === "PERSONAL_PROXY" ? "#1677ff" : "#E5E7EB"}`,
              background: pickupAuthType === "PERSONAL_PROXY" ? "#EFF6FF" : "#fff",
              borderRadius: 8, marginBottom: 10, overflow: "hidden",
              transition: "border-color 0.15s, background-color 0.15s",
            }}>
              <div onClick={() => handleCardSelect("PERSONAL_PROXY")}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", cursor: "pointer" }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 20, flexShrink: 0,
                  background: pickupAuthType === "PERSONAL_PROXY" ? "#1677ff1a" : "#F3F4F6",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {pickupAuthType === "PERSONAL_PROXY"
                    ? <CheckCircleFilled style={{ fontSize: 18, color: "#1677ff" }} />
                    : <TeamOutlined style={{ fontSize: 16, color: "#6B7280" }} />}
                </div>
                <div>
                  <Text strong style={{ display: "block", fontSize: 14, lineHeight: 1.4,
                    color: pickupAuthType === "PERSONAL_PROXY" ? "#1677ff" : "#111827" }}>
                    Authorize a personal representative
                  </Text>
                  <Text style={{ fontSize: 12, color: "#6B7280" }}>
                    Someone you trust will collect on your behalf
                  </Text>
                </div>
              </div>

              {/* Proxy field expansion — lives inside the card */}
              {pickupAuthType === "PERSONAL_PROXY" && (
                <div style={{ padding: "0 16px 16px", borderTop: "1px solid #DBEAFE" }}>
                  <div style={{ height: 16 }} />
                  <Form.Item name="pickup_proxy_name" label="Representative Name"
                    style={{ marginBottom: 12 }}
                    rules={[{ required: true, message: "Enter the representative's name." }]}>
                    <Input placeholder="Full name of the person collecting" />
                  </Form.Item>
                  <Form.Item name="pickup_proxy_id" label="Representative ID Document"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Enter the representative's ID number." }]}>
                    <Input placeholder="Passport / National ID / License number" />
                  </Form.Item>
                </div>
              )}
            </div>

            {/* ── Card 3: Partner (only if partners exist) ── */}
            {partnerOrgs.length > 0 && (
              <div style={{
                border: `2px solid ${pickupAuthType === "PARTNER_ORG" ? "#722ed1" : "#E5E7EB"}`,
                background: pickupAuthType === "PARTNER_ORG" ? "#F9F0FF" : "#fff",
                borderRadius: 8, marginBottom: 10, overflow: "hidden",
                transition: "border-color 0.15s, background-color 0.15s",
              }}>
                <div onClick={() => handleCardSelect("PARTNER_ORG")}
                  style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", cursor: "pointer" }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 20, flexShrink: 0,
                    background: pickupAuthType === "PARTNER_ORG" ? "#722ed11a" : "#F3F4F6",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {pickupAuthType === "PARTNER_ORG"
                      ? <CheckCircleFilled style={{ fontSize: 18, color: "#722ed1" }} />
                      : <ShopOutlined style={{ fontSize: 16, color: "#6B7280" }} />}
                  </div>
                  <div>
                    <Text strong style={{ display: "block", fontSize: 14, lineHeight: 1.4,
                      color: pickupAuthType === "PARTNER_ORG" ? "#722ed1" : "#111827" }}>
                      Authorize a pickup &amp; delivery partner
                    </Text>
                    <Text style={{ fontSize: 12, color: "#6B7280" }}>
                      A registered service will collect and deliver your medication
                    </Text>
                  </div>
                </div>

                {/* Partner expansion — hidden Form.Item + visual partner cards */}
                {pickupAuthType === "PARTNER_ORG" && (
                  <div style={{ padding: "0 16px 16px", borderTop: "1px solid #E9D8FD" }}>
                    {/* Hidden field — value set via handleCardSelect + partner card click */}
                    <Form.Item name="pickup_partner_org_id" style={{ display: "none", margin: 0 }}>
                      <Input />
                    </Form.Item>

                    <div style={{ height: 14 }} />
                    <Text style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 10 }}>
                      {partnerOrgs.length === 1
                        ? "The following partner is available in your network:"
                        : "Select a pickup partner:"}
                    </Text>

                    {partnerOrgs.map(org => {
                      const isOrgSelected = watchedPartnerId === org.id;
                      return (
                        <div key={org.id}
                          onClick={() => pickupForm.setFieldsValue({ pickup_partner_org_id: org.id })}
                          style={{
                            border: `2px solid ${isOrgSelected ? "#722ed1" : "#DDD6FE"}`,
                            background: isOrgSelected ? "#F5F3FF" : "#FAFAFA",
                            borderRadius: 6, padding: "12px 14px",
                            cursor: partnerOrgs.length > 1 ? "pointer" : "default",
                            display: "flex", alignItems: "center", gap: 12,
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 18, flexShrink: 0,
                            background: "#722ed11a",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            <ShopOutlined style={{ fontSize: 15, color: "#722ed1" }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <Text strong style={{ fontSize: 13, color: "#111827" }}>{org.name}</Text>
                              <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>ID: {org.org_id}</Tag>
                            </div>
                            <Text style={{ fontSize: 11, color: "#6B7280" }}>Pickup &amp; delivery service</Text>
                          </div>
                          {isOrgSelected && (
                            <CheckCircleFilled style={{ fontSize: 16, color: "#722ed1", flexShrink: 0 }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Divider style={{ margin: "20px 0 16px" }} />

            {/* Preferred pharmacy — card-based picker */}
            <Text strong style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
              Preferred Pharmacy{" "}
              <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>(optional)</Text>
            </Text>
            <Text type="secondary" style={{ display: "block", fontSize: 12, fontStyle: "italic", marginBottom: 8 }}>
              Selecting a preferred pharmacy lets them prepare your medication in advance. This selection applies to this prescription only.
            </Text>
            {/* Hidden field — holds the selected pharmacy ID */}
            <Form.Item name="preferred_pharmacy_id" style={{ margin: 0, height: 0, overflow: "hidden" }}>
              <input type="hidden" />
            </Form.Item>
            {rxPharmaciesLoading ? (
              <div style={{ textAlign: "center", padding: "12px 0", color: "#8c8c8c", fontSize: 13 }}>
                Loading available pharmacies…
              </div>
            ) : (() => {
              const avail = rxPharmacies[pickupRx?.id] ?? [];
              const inFormularyOk  = avail.filter(p => p.has_in_formulary && !p.out_of_stock_for_this_rx);
              const otherOk        = avail.filter(p => !p.has_in_formulary && !p.out_of_stock_for_this_rx);
              const oosPharmacies  = avail.filter(p => p.out_of_stock_for_this_rx);
              const fallback       = avail.length === 0 ? pharmacyList : [];

              const renderPharmacyCard = (ph, badge) => {
                const isSelected = watchedPharmacyId === ph.id;
                return (
                  <div key={ph.id}
                    onClick={() => pickupForm.setFieldsValue({ preferred_pharmacy_id: isSelected ? undefined : ph.id })}
                    style={{
                      border: `2px solid ${isSelected ? "#1677ff" : "#e6e6e6"}`,
                      background: isSelected ? "#e6f4ff" : "#fafafa",
                      borderRadius: 6,
                      padding: "8px 12px",
                      marginBottom: 6,
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                    <div>
                      <Text strong style={{ fontSize: 13 }}>{ph.pharmacy_name}</Text>
                      {ph.address ? <Text type="secondary" style={{ fontSize: 11, display: "block" }}>{ph.address}</Text> : null}
                      {ph.phone ? <Text type="secondary" style={{ fontSize: 11 }}>{ph.phone}</Text> : null}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                      {badge}
                      {ph.has_in_formulary && ph.quantity_in_stock != null && (
                        <Text style={{ fontSize: 11, color: ph.quantity_in_stock > 0 ? "#52c41a" : "#d46b08" }}>
                          {ph.quantity_in_stock} in stock
                        </Text>
                      )}
                      {isSelected && <Tag color="blue" style={{ marginRight: 0 }}>Selected</Tag>}
                    </div>
                  </div>
                );
              };

              return (
                <div>
                  {(inFormularyOk.length > 0 || otherOk.length > 0 || oosPharmacies.length > 0) ? (
                    <>
                      {inFormularyOk.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, padding: "4px 10px", marginBottom: 6 }}>
                            <Text style={{ fontSize: 12, color: "#389e0d", fontWeight: 600 }}>In Formulary — Ready to Fill</Text>
                          </div>
                          {inFormularyOk.map(ph => renderPharmacyCard(
                            ph,
                            ph.quantity_in_stock > 0
                              ? <Tag color="green"  style={{ marginRight: 0 }}>In stock</Tag>
                              : <Tag color="orange" style={{ marginRight: 0 }}>Low stock</Tag>
                          ))}
                        </div>
                      )}
                      {otherOk.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ background: "#fafafa", border: "1px solid #d9d9d9", borderRadius: 4, padding: "4px 10px", marginBottom: 6 }}>
                            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 600 }}>Other Pharmacies</Text>
                          </div>
                          {otherOk.map(ph => renderPharmacyCard(ph, null))}
                        </div>
                      )}
                      {oosPharmacies.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4, padding: "4px 10px", marginBottom: 6 }}>
                            <Text style={{ fontSize: 12, color: "#d46b08", fontWeight: 600 }}>Currently Unavailable</Text>
                          </div>
                          {oosPharmacies.map(ph => renderPharmacyCard(ph, <Tag color="orange" style={{ marginRight: 0 }}>Out of stock</Tag>))}
                        </div>
                      )}
                    </>
                  ) : (
                    fallback.map(ph => renderPharmacyCard(ph, null))
                  )}
                  {watchedPharmacyId && (
                    <Button size="small" type="link" style={{ padding: 0 }}
                      onClick={() => pickupForm.setFieldsValue({ preferred_pharmacy_id: undefined })}>
                      Clear selection
                    </Button>
                  )}
                </div>
              );
            })()}
          </Form>
        )}
      </Modal>

      {/* ── Contact Insurance Modal ── */}
      <Modal title="Contact Your Insurance" open={insuranceModal}
        onCancel={()=>setInsuranceModal(false)}
        footer={<Button type="primary" onClick={()=>setInsuranceModal(false)}>Got it</Button>}
 keyboard={true}>
        <Descriptions column={1} bordered size="small" style={{marginTop:8}}>
          <Descriptions.Item label="Insurance Carrier">{patientProfile?.insurance_carrier || "Not on file"}</Descriptions.Item>
          <Descriptions.Item label="Policy Number">{patientProfile?.insurance_policy_number || "Not on file"}</Descriptions.Item>
          <Descriptions.Item label="Patient PIN">{patientProfile?.pin || "—"}</Descriptions.Item>
        </Descriptions>
        <Alert type="info" showIcon
          message="Please contact your insurance carrier directly. Your patient PIN can be used for verification."
          style={{marginTop:16}} />
      </Modal>
    </Layout>
  );
}
